import React, {useState, useEffect} from 'react';
import styles from './TokenDistribution.module.css';

const Distribution_USDI = 'https://distribution-calculator-256611876551.europe-west1.run.app';

const TokenDistribution = () => {
    const [csvData, setCsvData] = useState([]);
    const [tokenAddress, setTokenAddress] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [distributionProgress, setDistributionProgress] = useState('');
    const [startStatusCheck, setStartStatusCheck] = useState(false);
    const [currentBatchCreatedTime, setCurrentBatchCreatedTime] = useState(null);
    const [currentBatchData, setCurrentBatchData] = useState([]); // 存储当前批次的数据用于比对

    // ------------------ 查询分发状态 ------------------
    useEffect(() => {
        let intervalId;
        if (startStatusCheck && currentBatchCreatedTime && tokenAddress) {
            // 立即查询一次
            fetchTokenDistributionStatus();
            // 每间隔10秒查询一次
            intervalId = setInterval(() => {
                fetchTokenDistributionStatus();
            }, 10000);
        }

        // 当组件卸载 或 startStatusCheck 变回 false 时，清除轮询
        return () => {
            if (intervalId) clearInterval(intervalId);
        };
    }, [startStatusCheck, currentBatchCreatedTime, tokenAddress]);

    const fetchTokenDistributionStatus = async () => {
        try {
            const response = await fetch(`${Distribution_USDI}/get-token-distributes`);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to fetch token distribution status: ${errorText}`);
            }
            const result = await response.json();

            if (result && result.data && tokenAddress && currentBatchCreatedTime) {
                setError(''); // 清除之前的错误

                // 处理时间格式，确保一致性
                const normalizeTime = (timeStr) => {
                    if (!timeStr) return null;

                    // 统一处理时间字符串
                    let normalizedStr = timeStr;

                    // 如果是后端返回的格式（如 2025-06-17T00:37:10.728000）
                    // 需要移除多余的微秒位并添加Z
                    if (normalizedStr.includes('.') && normalizedStr.length > 23) {
                        // 保留到毫秒级别（3位小数）
                        const [datePart, timePart] = normalizedStr.split('.');
                        normalizedStr = datePart + '.' + timePart.substring(0, 3);
                    }

                    // 如果没有时区信息，添加Z表示UTC
                    if (!normalizedStr.endsWith('Z') && !normalizedStr.includes('+') && !normalizedStr.includes('-')) {
                        normalizedStr += 'Z';
                    }

                    return new Date(normalizedStr).getTime();
                };

                const batchStartTime = normalizeTime(currentBatchCreatedTime);
                const timeWindowMs = 60000; // 60秒时间窗口

                // 筛选当前token地址和同一批次的分发记录
                const currentBatchDistributions = result.data.filter(item => {
                    if (item.token_mint_address !== tokenAddress) return false;

                    const itemCreatedTime = normalizeTime(item.created_time);
                    if (!itemCreatedTime) return false;

                    return Math.abs(itemCreatedTime - batchStartTime) <= timeWindowMs;
                });

                // 基于当前批次的地址列表来计算进度
                const total = currentBatchData.length || currentBatchDistributions.length;

                if (total > 0) {
                    // 计算已分发数量（有signature的记录）
                    const distributed = currentBatchDistributions.filter(
                        item => item.signature && item.signature.trim() !== ''
                    ).length;

                    setDistributionProgress(`${distributed}/${total}`);

                    // 如果全部分发完成，停止查询
                    if (distributed === total) {
                        setStartStatusCheck(false);
                        setSuccess(`Distribution completed! All ${total} transfers processed.`);
                        setCurrentBatchData([]); // 清空批次数据
                    }
                } else if (currentBatchDistributions.length === 0) {
                    // 如果还没有找到任何记录，显示初始化中
                    setDistributionProgress('Initializing...');
                }
            }
        } catch (error) {
            console.error('Error fetching token distribution status:', error);
            setError('Failed to fetch distribution status');
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (event) => {
            const text = event.target.result;
            const lines = text.trim().split('\n');
            const parsed = lines.map((line, idx) => {
                const [address, amount] = line.split(',');
                if (!address || !amount) {
                    setError(`Invalid format on line ${idx + 1}`);
                    return null;
                }
                return {address: address.trim(), amount: amount.trim()};
            }).filter(Boolean);

            setCsvData(parsed);
            setError('');
            setSuccess('');
            setDistributionProgress(''); // 清除之前的进度
            setCurrentBatchData([]); // 清除之前的批次数据
        };

        reader.readAsText(file);
    };

    const handleSubmit = async () => {
        if (!tokenAddress) {
            setError('Please enter a token mint address.');
            return;
        }

        if (csvData.length === 0) {
            setError('Please upload a CSV file with distribution data.');
            return;
        }

        // 生成当前批次的创建时间
        const createdTime = new Date().toISOString();
        setCurrentBatchCreatedTime(createdTime);
        setCurrentBatchData(csvData); // 保存当前批次的数据


        try {
            setError('');
            setSuccess('Submitting distribution...');
            setDistributionProgress(`0/${csvData.length}`); // 立即显示初始进度

            const response = await fetch(`${Distribution_USDI}/token_distribute_confirmed`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    token_mint_address: tokenAddress,
                    created_time: createdTime,
                    data: csvData,
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Request failed');
            }

            setSuccess('Distribution submitted successfully! Monitoring progress...');
            setError('');

            // 提交成功后开始状态查询
            setStartStatusCheck(true);

            // 30秒后自动停止查询（作为保险）
            setTimeout(() => {
                if (startStatusCheck) {
                    setStartStatusCheck(false);
                    if (distributionProgress !== `${csvData.length}/${csvData.length}`) {
                        setSuccess('Auto-stopped monitoring after 30 seconds. You can restart if needed.');
                    }
                }
            }, 300000); // 5分钟超时

        } catch (err) {
            setError('Submission failed: ' + err.message);
            setSuccess('');
            setStartStatusCheck(false);
            setCurrentBatchCreatedTime(null);
            setCurrentBatchData([]);
            setDistributionProgress('');
        }
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.heading}>Token Distribution (New)</h2>

            <input
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className={styles.input}
                disabled={startStatusCheck}
            />

            <input
                type="text"
                placeholder="Enter Token Mint Address (token needs to have 6 decimal places)"
                value={tokenAddress}
                onChange={(e) => setTokenAddress(e.target.value)}
                className={styles.input}
                disabled={startStatusCheck}
            />

            {error && <div className={styles.error}>{error}</div>}
            {success && <div className={styles.success}>{success}</div>}

            {/* 分发进度显示 */}
            {distributionProgress && (
                <div className={styles.progressContainer}>
                    <div className={styles.progress}>
                        Distribution Progress: {distributionProgress}
                    </div>
                </div>
            )}

            {csvData.length > 0 && (
                <>
                    <div className={styles.summary}>
                        Total addresses to distribute: {csvData.length}
                    </div>
                    <table className={styles.table}>
                        <thead>
                        <tr>
                            <th style={{color: 'black'}}>Address</th>
                            <th style={{color: 'black'}}>Amount</th>
                        </tr>
                        </thead>
                        <tbody>
                        {csvData.slice(0, 10).map((row, idx) => (
                            <tr key={idx}>
                                <td>{row.address}</td>
                                <td>{row.amount}</td>
                            </tr>
                        ))}
                        {csvData.length > 10 && (
                            <tr>
                                <td colSpan="2" style={{textAlign: 'center', fontStyle: 'italic'}}>
                                    ... and {csvData.length - 10} more addresses
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </>
            )}

            <a
                href="/sample.csv"
                download
                className={styles.downloadCSVButton}
            >
                Download Sample CSV
            </a>

            <button
                className={styles.submitButton}
                onClick={handleSubmit}
                disabled={startStatusCheck || csvData.length === 0}
            >
                {startStatusCheck ? 'Distribution in Progress...' : 'Submit'}
            </button>
        </div>
    );
};

export default TokenDistribution;